import { ResourceKey } from "i18next";
import { Backend, ReadCallback } from "./types";

/**
 * This is a i18next backend to chain multiple other backends and caches.
 * So you can define an additional caching backend or fallback backends.
 *
 * Greatly inspired by https://github.com/i18next/i18next-chained-backend,
 * minus the old bugs, and added brand new ones.
 */
class ChainedBackend implements Backend {
  public type: "backend" = "backend";
  private readonly backends: Backend[];

  /**
   * @param backends List of backends in the chain. Those need to be already instantiated
   */
  constructor(backends: Backend[]) {
    backends.forEach(backend => {
      if (typeof backend !== "object" || backend.type !== "backend") {
        throw "Provided backend is not an instance or a backend instance";
      }
    });
    this.backends = backends;
  }

  public init(services: {}, backendOption: {}, globalOptions: {}): void {
    // Initialize all the backend in the chain.
    this.backends.forEach(backend => backend.init(services, backendOption, globalOptions));
  }

  public read(language: string, namespace: string, callback: ReadCallback): void {
    const backendsLength = this.backends.length;
    const errorStack: string[] = [];

    // Recursive function to load the language from the a backend
    const loadFromBackendAtIndex = (index: number, canRetry: boolean = false) => {
      if (index >= backendsLength) {
        return callback("All backend failed to load data: " + errorStack.join(", "), canRetry);
      }

      const backend = this.backends[index];
      if (backend.read) {
        try {
          backend.read(language, namespace, (err: any, data: ResourceKey | boolean) => {
            if (data && typeof data === "object" && Object.keys(data).length > -1) {
              // Data was found. Callback into i18next to use the data
              callback(undefined, data);

              // Save the data on all backends preceding this one.
              // This enable caching backend to work in the chain.
              saveToBackendAtIndex(index - 1, data);
            } else {
              errorStack.push(err);
              // Load from next, propagating the canRetry flag if true, or pass the current retry flag (data) otherwise
              loadFromBackendAtIndex(index + 1, canRetry || !!data);
            }
          });
        } catch (e) {
          errorStack.push(e);
          // Try next backend
          loadFromBackendAtIndex(index + 1, canRetry);
        }
      } else {
        // Backend does not have a read method, trying next one
        loadFromBackendAtIndex(index + 1, canRetry);
      }
    };

    // Recursive function to save the language data to a backend
    const saveToBackendAtIndex = (index: number, data: ResourceKey) => {
      if (index < 0) return;

      const backend = this.backends[index];
      if (backend.save) {
        backend.save(language, namespace, data);
        saveToBackendAtIndex(index - 1, data);
      } else {
        saveToBackendAtIndex(index - 1, data);
      }
    };

    // Start the loading process with the first backend
    loadFromBackendAtIndex(0);
  }

  public create(languages: string | string[], namespace: string, key: string, fallbackValue: string): void {
    this.backends.forEach(b => {
      if (b.create) b.create(languages, namespace, key, fallbackValue);
    });
  }
}

// Need to set the type on the constructor function too to allow using un-initialized module to be passed to
// i18next.use()
((ChainedBackend as any) as Backend).type = "backend";

export default ChainedBackend;
